import React, { useEffect, useState } from 'react';
import { payrollAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiSearch, FiUsers, FiRotateCcw, FiEye, FiDollarSign } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';

const Payroll = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [payslipDetails, setPayslipDetails] = useState(null);
  
  const [searchCriteria, setSearchCriteria] = useState({
    role_id: '',
    month: '',
    year: new Date().getFullYear()
  });

  const [paymentData, setPaymentData] = useState({
    payslip_id: '',
    staff_name: '',
    amount: '',
    month_year: '',
    payment_mode: '',
    payment_date: new Date().toISOString().split('T')[0],
    remark: ''
  });

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await payrollAPI.getRoles();
      if (response.data.success) {
        setRoles(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching roles');
      console.error(error);
    }
  };

  const handleSearch = async () => {
    if (!searchCriteria.role_id || !searchCriteria.month || !searchCriteria.year) {
      toast.error('Please select role, month, and year');
      return;
    }

    setLoading(true);
    try {
      const response = await payrollAPI.getStaffList(searchCriteria);
      if (response.data.success) {
        setStaffList(response.data.data);
        
        if (response.data.data.length === 0) {
          toast.info('No staff found for this role');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching staff list');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePayroll = (staffId) => {
    navigate(`/hrms/payroll/create/${searchCriteria.month}/${searchCriteria.year}/${staffId}`);
  };

  const handleProceedToPay = (staff) => {
    setPaymentData({
      payslip_id: staff.payslip_id,
      staff_name: `${staff.name} ${staff.surname} (${staff.employee_id})`,
      amount: staff.net_salary,
      month_year: `${searchCriteria.month}-${searchCriteria.year}`,
      payment_mode: '',
      payment_date: new Date().toISOString().split('T')[0],
      remark: ''
    });
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async () => {
    if (!paymentData.payment_mode) {
      toast.error('Please select payment mode');
      return;
    }

    try {
      const response = await payrollAPI.processPayment({
        payslip_id: paymentData.payslip_id,
        payment_mode: paymentData.payment_mode,
        payment_date: paymentData.payment_date,
        remark: paymentData.remark
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setShowPaymentModal(false);
        handleSearch(); // Refresh the list
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error processing payment');
    }
  };

  const handleViewPayslip = async (payslipId) => {
    try {
      const response = await payrollAPI.getPayslip(payslipId);
      if (response.data.success) {
        const data = response.data.data;
        // Ensure arrays exist
        data.allowance_types = data.allowance_types || [];
        data.allowance_amounts = data.allowance_amounts || [];
        data.deduction_types = data.deduction_types || [];
        data.deduction_amounts = data.deduction_amounts || [];
        setPayslipDetails(data);
        setShowPayslipModal(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching payslip');
    }
  };

  const handleRevert = async (payslipId, status) => {
    const action = status === 'Paid' ? 'revert to generated status' : 'delete';
    if (!window.confirm(`Are you sure you want to ${action} this payslip?`)) {
      return;
    }

    try {
      const response = await payrollAPI.revertPayslip(payslipId);
      if (response.data.success) {
        toast.success(response.data.message);
        handleSearch(); // Refresh the list
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error reverting payslip');
    }
  };

  const roleOptions = roles.map(role => ({
    value: role.id,
    label: role.name
  }));

  const monthOptions = months.map(month => ({
    value: month,
    label: month
  }));

  const yearOptions = years.map(year => ({
    value: year,
    label: year.toString()
  }));

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          <FiUsers className="inline mr-2" /> Human Resource
        </h1>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        {/* Search Criteria Section */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            <FiSearch className="inline mr-2" /> Select Criteria
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <Select
                options={roleOptions}
                value={roleOptions.find(opt => opt.value === searchCriteria.role_id)}
                onChange={(option) => setSearchCriteria({ ...searchCriteria, role_id: option ? option.value : '' })}
                placeholder="Select"
                isClearable
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Month
              </label>
              <Select
                options={monthOptions}
                value={monthOptions.find(opt => opt.value === searchCriteria.month)}
                onChange={(option) => setSearchCriteria({ ...searchCriteria, month: option ? option.value : '' })}
                placeholder="Select"
                isClearable
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year
              </label>
              <Select
                options={yearOptions}
                value={yearOptions.find(opt => opt.value === searchCriteria.year)}
                onChange={(option) => setSearchCriteria({ ...searchCriteria, year: option ? option.value : '' })}
                placeholder="Select"
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md transition-colors"
            >
              <FiSearch className="inline mr-2" />
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>

        {/* Staff List Section */}
        {staffList.length > 0 && (
          <>
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                <FiUsers className="inline mr-2" /> Staff List
              </h3>
            </div>

            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Staff ID</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Department</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Designation</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Phone</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffList.map((staff) => (
                      <tr key={staff.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{staff.employee_id}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{staff.name} {staff.surname}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{staff.role_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{staff.department_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{staff.designation_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{staff.contact_no}</td>
                        <td className="px-4 py-3">
                          {staff.status && (
                            <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                              staff.status === 'Paid' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {staff.status}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="flex justify-end gap-2">
                            {/* Show Revert button for both generated and Paid status */}
                            {staff.status && (
                              <button
                                onClick={() => handleRevert(staff.payslip_id, staff.status)}
                                className="inline-block px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs rounded"
                                title="Revert"
                              >
                                <FiRotateCcw className="inline" />
                              </button>
                            )}
                            
                            {/* Show Generate Payroll button when no status */}
                            {!staff.status && (
                              <button
                                onClick={() => handleGeneratePayroll(staff.id)}
                                className="px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white text-xs rounded"
                              >
                                Generate Payroll
                              </button>
                            )}
                            
                            {/* Show Proceed To Pay button for generated status */}
                            {staff.status === 'generated' && (
                              <button
                                onClick={() => handleProceedToPay(staff)}
                                className="px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white text-xs rounded"
                              >
                                Proceed To Pay
                              </button>
                            )}
                            
                            {/* Show View Payslip button for Paid status */}
                            {staff.status === 'Paid' && (
                              <button
                                onClick={() => handleViewPayslip(staff.payslip_id)}
                                className="px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white text-xs rounded"
                              >
                                <FiEye className="inline mr-1" /> View Payslip
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {!loading && staffList.length === 0 && searchCriteria.role_id && (
          <div className="p-12 text-center">
            <FiUsers size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Staff Found</h3>
            <p className="text-gray-600">
              No staff members found for the selected criteria.
            </p>
          </div>
        )}

        {!searchCriteria.role_id && staffList.length === 0 && (
          <div className="p-12 text-center">
            <FiSearch size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Select Criteria</h3>
            <p className="text-gray-600">
              Please select role, month, and year to view staff payroll.
            </p>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-5" onClick={() => setShowPaymentModal(false)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-8 py-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Proceed To Pay</h2>
            </div>
            <div className="p-8 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Staff</label>
                  <input
                    type="text"
                    value={paymentData.staff_name}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Amount</label>
                  <input
                    type="text"
                    value={`₹${parseFloat(paymentData.amount).toFixed(2)}`}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Month Year</label>
                  <input
                    type="text"
                    value={paymentData.month_year}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Mode <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={paymentData.payment_mode}
                    onChange={(e) => setPaymentData({ ...paymentData, payment_mode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                  >
                    <option value="">Select</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                    <option value="online">Transfer to Bank Account</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date</label>
                  <input
                    type="date"
                    value={paymentData.payment_date}
                    onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
                  <textarea
                    value={paymentData.remark}
                    onChange={(e) => setPaymentData({ ...paymentData, remark: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                  />
                </div>
              </div>
            </div>
            <div className="px-8 py-5 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePaymentSubmit}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payslip View Modal */}
      {showPayslipModal && payslipDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-5" onClick={() => setShowPayslipModal(false)}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center print:hidden">
              <h2 className="text-xl font-semibold text-gray-900">Details</h2>
              <button
                onClick={() => window.print()}
                className="px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-700 text-sm rounded border border-gray-300"
              >
                <i className="fa fa-print mr-1"></i>Print
              </button>
            </div>
            <div className="p-8 overflow-y-auto">
              {/* Header */}
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Payslip for the period of {payslipDetails.month} {payslipDetails.year}
                </h3>
                <div className="flex justify-between items-center text-sm text-gray-600 mb-4">
                  <div>Payslip #{payslipDetails.id}</div>
                  <div>Payment Date: {payslipDetails.payment_date ? new Date(payslipDetails.payment_date).toLocaleDateString('en-GB') : '-'}</div>
                </div>
              </div>
              
              <hr className="mb-6" />
              
              {/* Staff Details */}
              <div className="mb-6">
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b">
                      <th className="py-2 text-left font-semibold w-1/4">Staff ID</th>
                      <td className="py-2 w-1/4">{payslipDetails.employee_id}</td>
                      <th className="py-2 text-left font-semibold w-1/4">Name</th>
                      <td className="py-2 w-1/4 capitalize">{payslipDetails.name} {payslipDetails.surname}</td>
                    </tr>
                    <tr>
                      <th className="py-2 text-left font-semibold">Department</th>
                      <td className="py-2">{payslipDetails.department_name || '-'}</td>
                      <th className="py-2 text-left font-semibold">Designation</th>
                      <td className="py-2">{payslipDetails.designation_name || '-'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {/* Earnings and Deductions Table */}
              <div className="mb-6">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="py-2 px-3 text-left font-semibold border w-[23%]">Earning</th>
                      <th className="py-2 px-3 text-right font-semibold border w-[19%]">Amount(₹)</th>
                      <th className="py-2 px-3 text-left font-semibold border w-[23%]">Deduction</th>
                      <th className="py-2 px-3 text-right font-semibold border w-[19%]">Amount(₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Dynamic rows for earnings and deductions */}
                    {(() => {
                      const maxRows = Math.max(
                        (payslipDetails.allowance_types?.length || 0),
                        (payslipDetails.deduction_types?.length || 0)
                      );
                      const rows = [];
                      for (let i = 0; i < maxRows; i++) {
                        rows.push(
                          <tr key={i} className="border-b">
                            <td className="py-2 px-3 border">{payslipDetails.allowance_types?.[i] || ''}</td>
                            <td className="py-2 px-3 text-right border">
                              {payslipDetails.allowance_amounts?.[i] ? parseFloat(payslipDetails.allowance_amounts[i]).toFixed(0) : ''}
                            </td>
                            <td className="py-2 px-3 border">{payslipDetails.deduction_types?.[i] || ''}</td>
                            <td className="py-2 px-3 text-right border">
                              {payslipDetails.deduction_amounts?.[i] ? parseFloat(payslipDetails.deduction_amounts[i]).toFixed(0) : ''}
                            </td>
                          </tr>
                        );
                      }
                      return rows;
                    })()}
                    
                    {/* Total row */}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="py-2 px-3 border">Total Earning</td>
                      <td className="py-2 px-3 text-right border">{parseFloat(payslipDetails.total_allowance || 0).toFixed(0)}</td>
                      <td className="py-2 px-3 border">Total Deduction</td>
                      <td className="py-2 px-3 text-right border">{parseFloat(payslipDetails.total_deduction || 0).toFixed(0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {/* Summary Table */}
              <div className="mb-6">
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b">
                      <th className="py-2 text-left font-semibold w-1/3">Payment Mode</th>
                      <td className="py-2 text-right">{payslipDetails.payment_mode || '-'}</td>
                    </tr>
                    <tr className="border-b">
                      <th className="py-2 text-left font-semibold">Basic Salary(₹)</th>
                      <td className="py-2 text-right">{parseFloat(payslipDetails.basic || 0).toFixed(0)}</td>
                    </tr>
                    <tr className="border-b">
                      <th className="py-2 text-left font-semibold">Gross Salary(₹)</th>
                      <td className="py-2 text-right">{parseFloat(payslipDetails.gross_salary || 0).toFixed(0)}</td>
                    </tr>
                    <tr className="border-b">
                      <th className="py-2 text-left font-semibold">Net Salary(₹)</th>
                      <td className="py-2 text-right font-bold text-lg">{parseFloat(payslipDetails.net_salary || 0).toFixed(0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {/* Footer Note */}
              <div className="text-sm text-gray-600 mb-4">
                This payslip is computer generated hence no signature is required.
              </div>
              
              {/* Payment Remarks */}
              {payslipDetails.remark && (
                <div className="text-sm text-gray-700">
                  <strong>Payment Remarks:</strong> {payslipDetails.remark}
                </div>
              )}
            </div>
            <div className="px-8 py-4 border-t border-gray-200 flex justify-end print:hidden">
              <button
                onClick={() => setShowPayslipModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payroll;
